import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

const Lightbox = ({ media, initialIndex, onClose }) => {
	const [index, setIndex] = useState(initialIndex);
	const [slideshowPlaying, setSlideshowPlaying] = useState(false);
	const [slideshowInterval, setSlideshowInterval] = useState(5);
	const slideshowTimer = useRef(null);

	const currentItem = media[index];

	const navigate = useCallback(
		(direction) => {
			setIndex((prev) => (prev + direction + media.length) % media.length);
		},
		[media.length]
	);

	const toggleSlideshow = useCallback(() => {
		setSlideshowPlaying((prev) => !prev);
	}, []);

	const handleClose = useCallback(() => {
		setSlideshowPlaying(false);
		if (slideshowTimer.current) clearInterval(slideshowTimer.current);
		onClose();
	}, [onClose]);

	// Slideshow timer
	useEffect(() => {
		if (slideshowPlaying) {
			slideshowTimer.current = setInterval(() => {
				navigate(1);
			}, slideshowInterval * 1000);
		} else if (slideshowTimer.current) {
			clearInterval(slideshowTimer.current);
		}
		return () => {
			if (slideshowTimer.current) clearInterval(slideshowTimer.current);
		};
	}, [slideshowPlaying, slideshowInterval, navigate]);

	// Keyboard handlers
	useEffect(() => {
		const handleKeyDown = (e) => {
			switch (e.key) {
				case 'ArrowLeft':
					navigate(-1);
					break;
				case 'ArrowRight':
					navigate(1);
					break;
				case ' ':
					e.preventDefault();
					toggleSlideshow();
					break;
				case 'Escape':
					handleClose();
					break;
				default:
					break;
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [navigate, toggleSlideshow, handleClose]);

	if (!currentItem) return null;

	return createPortal(
		<div className="gallery-lightbox" onClick={handleClose}>
			<button
				className="lightbox-nav prev"
				onClick={(e) => {
					e.stopPropagation();
					navigate(-1);
				}}
				type="button"
			>
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<polyline points="15 18 9 12 15 6" />
				</svg>
			</button>
			<button
				className="lightbox-nav next"
				onClick={(e) => {
					e.stopPropagation();
					navigate(1);
				}}
				type="button"
			>
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<polyline points="9 18 15 12 9 6" />
				</svg>
			</button>
			{/* Top Controls */}
			<div className="lightbox-top-controls">
				<div className="slideshow-controls">
					<button
						className="slideshow-btn"
						onClick={(e) => {
							e.stopPropagation();
							toggleSlideshow();
						}}
						type="button"
					>
						{slideshowPlaying ? (
							<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
								<rect x="6" y="4" width="4" height="16" />
								<rect x="14" y="4" width="4" height="16" />
							</svg>
						) : (
							<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
								<polygon points="5 3 19 12 5 21 5 3" />
							</svg>
						)}
					</button>
					<div className="slideshow-timer" onClick={(e) => e.stopPropagation()}>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<circle cx="12" cy="12" r="10" />
							<polyline points="12 6 12 12 16 14" />
						</svg>
						<select
							className="slideshow-select"
							onChange={(e) => setSlideshowInterval(Number(e.target.value))}
							value={slideshowInterval}
						>
							<option value={2}>2s</option>
							<option value={5}>5s</option>
							<option value={10}>10s</option>
						</select>
					</div>
				</div>
				<button className="lightbox-close" onClick={handleClose} type="button">
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<line x1="18" y1="6" x2="6" y2="18" />
						<line x1="6" y1="6" x2="18" y2="18" />
					</svg>
				</button>
			</div>
			{currentItem.type === 'video' ? (
				<video
					autoPlay
					className="lightbox-image"
					controls
					onClick={(e) => e.stopPropagation()}
					src={currentItem.link}
				/>
			) : (
				<img
					alt=""
					className="lightbox-image"
					onClick={(e) => e.stopPropagation()}
					src={currentItem.link}
				/>
			)}
			{slideshowPlaying && (
				<div className="slideshow-status" onClick={(e) => e.stopPropagation()}>
					<span className="slideshow-dot" />
					<span>Slideshow playing</span>
				</div>
			)}
			<div className="lightbox-info">
				{currentItem.student_name && (
					<div className="info-student">{currentItem.student_name}</div>
				)}
				{(currentItem.room_name || currentItem.activity_name) && (
					<div className="info-context">
						{[currentItem.room_name, currentItem.activity_name]
							.filter(Boolean)
							.join(' \u2022 ')}
					</div>
				)}
				{currentItem.sort_date && (
					<div className="info-date">
						{new Date(currentItem.sort_date).toLocaleDateString('en-US', {
							month: 'short',
							day: 'numeric',
							year: 'numeric',
						})}
					</div>
				)}
			</div>
		</div>,
		document.body
	);
};

export default Lightbox;
